import React from 'react';
import { Card, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

export interface StatusBarItem {
  key: string;
  label: string;
  count: number;
  color: string;
  /** Optional link target (querystring appended to WO list path) */
  drillPath?: string;
}

export interface StatusBarChartProps {
  title: string;
  items: StatusBarItem[];
  testId?: string;
}

/**
 * Horizontal CSS bar chart mirroring PipelineBar.tsx (D-G-5).
 * Used for woByStatus and techProductivity sections.
 * Clicking a bar navigates to drillPath if provided.
 */
const StatusBarChart: React.FC<StatusBarChartProps> = ({ title, items, testId }) => {
  const navigate = useNavigate();
  const max = Math.max(1, ...items.map((i) => i.count));

  return (
    <Card title={title} size="small" data-testid={testId}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item) => {
          const widthPct = Math.round((item.count / max) * 100);
          const clickable = Boolean(item.drillPath);

          return (
            <button
              key={item.key}
              type="button"
              aria-label={item.label}
              data-testid={`${testId}-bar-${item.key}`}
              onClick={() => {
                if (item.drillPath) navigate(item.drillPath);
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr 36px',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                border: 'none',
                padding: '4px 0',
                cursor: clickable ? 'pointer' : 'default',
                textAlign: 'left',
              }}
            >
              <Text style={{ fontSize: 13 }}>{item.label}</Text>
              <div
                style={{
                  height: 10,
                  background: '#f0f0f0',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background: item.color,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <Text strong style={{ textAlign: 'right', fontSize: 13 }}>
                {item.count}
              </Text>
            </button>
          );
        })}
        {items.length === 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>No data for this range.</Text>
        )}
      </div>
    </Card>
  );
};

export default StatusBarChart;
